import Stores from '../config/stores';

const normalizeToken = (value) => String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '');

const toNumber = (value) => {
    const number = Number(value);
    return Number.isFinite(number) ? number : null;
};

const getCandidateList = (weeklyData) => {
    const candidates = [
        ...(Array.isArray(weeklyData?.ranked_candidates) ? weeklyData.ranked_candidates : []),
        ...(Array.isArray(weeklyData?.top_recommendations) ? weeklyData.top_recommendations : []),
    ];

    return candidates.filter((candidate, index, array) => {
        const recipeId = String(candidate?.recipe_id || '').trim();
        return recipeId && array.findIndex(
            (otherCandidate) => String(otherCandidate?.recipe_id || '').trim() === recipeId
        ) === index;
    });
};

const getPreferredStoreTokens = (preferredStoreKeys) => new Set(
    (Array.isArray(preferredStoreKeys) ? preferredStoreKeys : [])
        .flatMap((storeKey) => [storeKey, Stores[storeKey]?.name])
        .map(normalizeToken)
        .filter(Boolean)
);

export const findFavoriteDealMatches = (weeklyData, profile) => {
    const favoriteIds = new Set(
        (Array.isArray(profile?.favoriteRecipes) ? profile.favoriteRecipes : [])
            .map((favorite) => String(favorite?.recipeId || favorite || '').trim())
            .filter(Boolean)
    );
    const favoriteTitleById = new Map(
        (Array.isArray(profile?.favoriteRecipes) ? profile.favoriteRecipes : [])
            .map((favorite) => [
                String(favorite?.recipeId || favorite || '').trim(),
                String(favorite?.title || favorite?.recipeId || favorite || '').trim(),
            ])
    );
    const threshold = Math.max(5, Math.min(90, Number(profile?.favoriteDealThreshold) || 50));
    const preferredStoreTokens = getPreferredStoreTokens(profile?.preferredStoreKeys);

    return getCandidateList(weeklyData)
        .filter((candidate) => favoriteIds.has(String(candidate.recipe_id)))
        .map((candidate) => {
            const qualifyingOffers = (Array.isArray(candidate.matched_offers) ? candidate.matched_offers : [])
                .filter((offer) => {
                    const discount = toNumber(offer?.rabat_pct);
                    const storeMatches = preferredStoreTokens.size === 0
                        || preferredStoreTokens.has(normalizeToken(offer?.butik));
                    return discount !== null && discount >= threshold && storeMatches;
                })
                .sort((left, right) => Number(right.rabat_pct || 0) - Number(left.rabat_pct || 0));

            return {
                recipeId: String(candidate.recipe_id),
                title: favoriteTitleById.get(String(candidate.recipe_id))
                    || String(candidate.titel || candidate.recipe_id),
                offers: qualifyingOffers.map((offer) => ({
                    ingredient: String(offer.produkt_navn || offer.tilbudsnavn || 'Ingrediens'),
                    offerName: String(offer.tilbudsnavn || offer.produkt_navn || 'Tilbud'),
                    store: String(offer.butik || 'Butik'),
                    discountPercentage: Number(offer.rabat_pct || 0),
                    offerPrice: toNumber(offer.tilbudspris_dkk),
                    normalPrice: toNumber(offer.normalpris_dkk),
                })),
            };
        })
        .filter((match) => match.offers.length > 0)
        .sort((left, right) => (
            Math.max(...right.offers.map((offer) => offer.discountPercentage))
            - Math.max(...left.offers.map((offer) => offer.discountPercentage))
        ));
};

export const buildFavoriteDealNotificationKey = (weeklyData, profile, matches) => [
    String(weeklyData?.week_id || 'current-week'),
    String(profile?.favoriteDealThreshold || 50),
    ...matches.map((match) => `${match.recipeId}:${match.offers.length}`),
].join('|');
